<!-- =========================================================================================
  File Name: ClusterList.vue
  Description: List of all clustering records with CRUD actions
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-23
========================================================================================== -->

<template>
	<div class="cluster-list-sidebar">
		<vx-card :title="$t('Clustering.ClusterList')" class="mb-0 p-2">
			<!-- Close Button -->
			<template slot="actions">
				<feather-icon icon="XIcon" svgClasses="w-5 h-5 cursor-pointer" class="ml-2" @click="$emit('close')" />
			</template>

			<div class="list-content">
				<!-- Search Bar -->
				<div class="search-section mb-4">
					<div class="flex gap-2">
						<vs-input
							v-model="searchQuery"
							:placeholder="$t('Clustering.SearchClusters')"
							class="flex-1"
							icon-pack="feather"
							icon="icon-search"
							@input="onSearch"
						/>
						<vs-button
							v-if="false"
							color="primary"
							type="border"
							icon-pack="feather"
							icon="icon-filter"
							:title="$t('FiltersAdvanced.Title')"
							@click="toggleFilterPanel"
						></vs-button>
						<vs-button
							color="primary"
							type="border"
							icon-pack="feather"
							icon="icon-refresh-cw"
							:title="$t('Refresh')"
							@click="refreshClusters"
						></vs-button>
						<vs-button
							color="primary"
							type="border"
							icon-pack="feather"
							icon="icon-plus circle"
							:title="$t('Clustering.CreateNew')"
							@click="toggleWizard"
						></vs-button>
					</div>
				</div>

				<!-- Advanced Filter Panel -->
				<transition name="slide-fade">
					<div v-if="showFilterPanel" class="filter-panel-wrapper mb-4">
						<dynamic-filter-panel
							:title="$t('FiltersAdvanced.Title')"
							:show-filters-config="true"
							:show-close-button="false"
							:search-config="filterSearchConfig"
							@applyFilters="handleApplyFilters"
							@clear="handleClearFilters"
						/>
					</div>
				</transition>

				<!-- Loading State -->
				<div v-if="isLoading" class="loading-state">
					<vs-progress indeterminate color="primary"></vs-progress>
					<p class="text-center text-grey mt-3">{{ $t("Loading") }}...</p>
				</div>

				<!-- Empty State -->
				<div v-else-if="clusters.length === 0" class="empty-state">
					<feather-icon icon="InboxIcon" svgClasses="w-16 h-16 text-grey" class="mb-3" />
					<p class="text-grey">{{ $t("Clustering.NoClustersFound") }}</p>
				</div>

				<!-- Cluster Items -->
				<div v-else class="clusters-container">
					<transition-group name="list" tag="div">
						<div v-for="cluster in filteredClusters" :key="cluster.id" class="cluster-item">
							<div class="cluster-header">
								<div class="cluster-info">
									<h6 class="cluster-name">{{ cluster.name }}</h6>
									<p class="cluster-meta">
										<span class="meta-item">
											<feather-icon icon="LayersIcon" svgClasses="w-3 h-3" />
											{{ cluster.total_clusters }} {{ $t("Clustering.Clusters") }}
										</span>
										<span class="meta-item">
											<feather-icon icon="LayersIcon" svgClasses="w-3 h-3" />
											{{ cluster.total_micro_clusters }} {{ $t("Clustering.MicroClusters") }}
										</span>
										<span class="meta-item">
											<feather-icon icon="MapPinIcon" svgClasses="w-3 h-3" />
											{{ cluster.total_points ? cluster.total_points : 0 }} {{ $t("Points") }}
										</span>
									</p>
									<p class="cluster-date">
										<feather-icon icon="CalendarIcon" svgClasses="w-3 h-3" />
										{{ formatDate(cluster.added_on) }}
									</p>
								</div>
							</div>
							<div class="cluster-actions" @click.stop>
								<vs-button
									color="success"
									type="border"
									icon-pack="feather"
									icon="icon-eye"
									size="small"
									:title="$t('Clustering.ViewOnMap')"
									@click="selectCluster(cluster)"
								></vs-button>
								<vs-button
									color="primary"
									type="border"
									icon-pack="feather"
									icon="icon-edit-2"
									size="small"
									:title="$t('Crud.Update')"
									@click="openEditDialog(cluster)"
								></vs-button>
								<vs-button
									color="warning"
									type="border"
									icon-pack="feather"
									icon="icon-download"
									size="small"
									:title="$t('Export')"
									@click="exportClustering(cluster)"
								></vs-button>
								<vs-button
									color="danger"
									type="border"
									icon-pack="feather"
									icon="icon-trash-2"
									size="small"
									:title="$t('Crud.Delete')"
									@click="$emit('delete', cluster)"
								></vs-button>
							</div>
						</div>
					</transition-group>
				</div>
			</div>
			<div slot="footer">
				<div v-if="totalPages > 1">
					<vs-pagination :total="totalPages" :max="5" v-model="currentPage" @input="onPageChange" />
				</div>
				<div class="font-bold text-sm">
					<vs-divider></vs-divider>
					<div class="flex justify-between items-center">
						<span>{{ $t("RouterSelection.Total") }}: {{ pagination.total }}</span>
						<span>{{ $t("Pages") }} {{ pagination.page }} / {{ totalPages }}</span>
					</div>
				</div>
			</div>
		</vx-card>

		<!-- Edit Clustering Name Prompt -->
		<vs-prompt
			:active.sync="showEditDialog"
			:title="$t('Clustering.EditName')"
			:accept-text="$t('Save')"
			:cancel-text="$t('Cancel')"
			:is-valid="isValidName"
			@accept="updateClusteringName"
		>
			<div class="con-exemple-prompt">
				<vs-input
					v-model="editingCluster.name"
					:placeholder="$t('Clustering.NamePlaceholder')"
					class="w-full mb-3"
					maxlength="125"
				/>
			</div>
		</vs-prompt>
	</div>
</template>

<script>
import DynamicFilterPanel from "./DynamicFilterPanel.vue";

export default {
	name: "ClusterList",
	components: {
		DynamicFilterPanel,
	},
	props: {
		clusters: {
			type: Array,
			default: () => [],
		},
		pagination: {
			type: Object,
			default: () => ({
				page: 1,
				quantity: 10,
				total: 0,
			}),
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		currentViewMode: {
			type: String,
			default: "clusters",
		},
	},
	data() {
		return {
			searchQuery: "",
			currentPage: 1,
			selectedClusterId: null,
			showFilterPanel: false,
			showEditDialog: false,
			editingCluster: {
				id: null,
				name: "",
			},
			filterSearchConfig: {
				fields: [
					"id",
					"name",
					"company_id",
					"success",
					"status",
					"added_on",
					"total_points",
					"total_clusters",
					"total_micro_clusters",
					"folder_path",
					"update_on",
				],
				page: 1,
				quantity: 10,
				order_sort: {id: "desc"},
			},
		};
	},
	computed: {
		filteredClusters() {
			if (!this.searchQuery) {
				return this.clusters;
			}

			const query = this.searchQuery.toLowerCase();
			return this.clusters.filter((cluster) => {
				return cluster.name.toLowerCase().includes(query);
			});
		},
		totalPages() {
			return Math.ceil(this.pagination.total / this.pagination.quantity);
		},
		isValidName() {
			return (
				this.editingCluster.name !== null &&
				this.editingCluster.name !== "" &&
				this.editingCluster.name.trim().length > 0
			);
		},
	},
	mounted() {
		this.refreshClusters();
	},
	methods: {
		selectCluster(cluster) {
			this.selectedClusterId = cluster.id;
			this.$emit("view-clusters-on-map", cluster);
			this.$emit("close");
		},
		exportClustering(cluster) {
			this.$emit("export-clustering", cluster);
		},
		onSearch() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.$emit("search", this.searchQuery);
			}, 300);
		},
		onPageChange(page) {
			this.$emit("page-change", page);
		},
		formatDate(dateString) {
			if (!dateString) return "-";

			try {
				const date = new Date(dateString);
				return date.toLocaleDateString(this.$i18n.locale, {
					year: "numeric",
					month: "short",
					day: "numeric",
				});
			} catch (error) {
				return dateString;
			}
		},
		toggleFilterPanel() {
			this.showFilterPanel = !this.showFilterPanel;
		},
		refreshClusters() {
			this.$emit("refresh-clusters", this.currentPage);
		},
		toggleWizard() {
			this.$emit("toggle-wizard");
		},
		handleApplyFilters(filterBody) {
			// Emit the filter body to parent component for API search
			this.$emit("apply-filters", filterBody);
			// Show feedback
			this.$vs.notify({
				title: this.$t("Success"),
				text: this.$t("FiltersAdvanced.Apply"),
				color: "primary",
				icon: "check_circle",
			});
		},
		handleClearFilters() {
			// Reset search query
			this.searchQuery = "";

			// Emit clear event to parent
			this.$emit("clear-filters");

			// Show feedback
			this.$vs.notify({
				title: this.$t("Success"),
				text: this.$t("FiltersAdvanced.Clear"),
				color: "warning",
				icon: "refresh",
			});
		},

		openEditDialog(cluster) {
			// Set the editing cluster data
			this.editingCluster = {
				id: cluster.id,
				name: cluster.name || "",
			};
			// Show the dialog
			this.showEditDialog = true;
		},
		async updateClusteringName() {
			this.$emit("update-clustering", {
				id: this.editingCluster.id,
				name: this.editingCluster.name.trim(),
			});
			this.showEditDialog = false;
			this.editingCluster = {
				id: null,
				name: "",
			};
		},
	},
	watch: {
		"pagination.page"(val) {
			this.currentPage = val;
		},
	},
};
</script>

<style lang="scss" scoped>
.cluster-list-sidebar {
	position: absolute;
	top: 5px;
	left: 10px;
	width: 420px;
	max-height: calc(100vh - 160px);
	z-index: 100;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	border-radius: 8px;

	.list-content {
		max-height: calc(100vh - 250px);
		overflow-y: auto;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-track {
			background: #f1f1f1;
			border-radius: 10px;
		}

		&::-webkit-scrollbar-thumb {
			background: #888;
			border-radius: 10px;

			&:hover {
				background: #555;
			}
		}
	}

	.loading-state,
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem 1rem;
	}

	.clusters-container {
		.cluster-item {
			background: white;
			border: 1px solid rgba(0, 0, 0, 0.08);
			border-radius: 8px;
			padding: 1rem;
			margin-bottom: 0.75rem;
			cursor: pointer;
			transition: all 0.3s ease;

			&:hover {
				box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
				transform: translateY(-2px);
				border-color: var(--vs-primary);
			}

			.cluster-header {
				display: flex;
				justify-content: space-between;
				align-items: flex-start;
				margin-bottom: 0.75rem;

				.cluster-info {
					flex: 1;

					.cluster-name {
						font-size: 1rem;
						font-weight: 600;
						margin-bottom: 0.5rem;
						color: #333;
					}

					.cluster-meta {
						display: flex;
						gap: 1rem;
						margin-bottom: 0.25rem;

						.meta-item {
							display: flex;
							align-items: center;
							gap: 0.25rem;
							font-size: 0.75rem;
							color: #666;
						}
					}

					.cluster-date {
						display: flex;
						align-items: center;
						gap: 0.25rem;
						font-size: 0.7rem;
						color: #999;
					}
				}
			}

			.cluster-actions {
				display: flex;
				gap: 0.5rem;
				justify-content: flex-end;
				padding-top: 0.5rem;
				border-top: 1px solid rgba(0, 0, 0, 0.06);
			}
		}
	}
	.filter-panel-wrapper {
		background: #f9fafb;
		border-radius: 8px;
		padding: 1rem;
		border: 1px solid rgba(0, 0, 0, 0.08);
	}
}

// List transition animations
.list-enter-active,
.list-leave-active {
	transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
	opacity: 0;
	transform: translateX(-20px);
}

// Filter panel slide animation
.slide-fade-enter-active {
	transition: all 0.3s ease;
}

.slide-fade-leave-active {
	transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
	transform: translateY(-10px);
	opacity: 0;
}
// Responsive
@media screen and (max-width: 768px) {
	.cluster-list-sidebar {
		width: calc(100% - 40px);
		left: 10px;
		right: 10px;
		max-height: 60vh;

		.list-content {
			max-height: calc(60vh - 120px);
		}

		.search-section {
			.flex {
				gap: 0.5rem;
			}
		}

		.view-mode-selector {
			.segmented-control {
				.segment-option {
					.segment-icon {
						width: 28px;
						height: 28px;
						margin-bottom: 0.5rem;

						::v-deep svg {
							width: 1rem;
							height: 1rem;
						}
					}

					.segment-content {
						.segment-title {
							font-size: 0.75rem;
						}

						.segment-subtitle {
							font-size: 0.625rem;
						}
					}
				}
			}
		}
	}
}

@media screen and (max-width: 480px) {
	.cluster-list-sidebar {
		width: calc(100% - 20px);
		left: 10px;
		right: 10px;
		top: 10px;

		.clusters-container {
			.cluster-item {
				.cluster-header {
					flex-direction: column;

					.cluster-info {
						.cluster-meta {
							flex-direction: column;
							gap: 0.25rem;
						}
					}
				}

				.cluster-actions {
					justify-content: center;
				}
			}
		}

		.view-mode-selector {
			padding: 0.75rem;

			.mode-header {
				margin-bottom: 0.5rem;
				.mode-label {
					font-size: 0.8125rem;
				}
			}

			.segmented-control {
				grid-template-columns: 1fr;
				gap: 0.5rem;

				.segment-option {
					flex-direction: row;
					justify-content: flex-start;
					.segment-icon {
						width: 28px;
						height: 28px;
						margin-bottom: 0;
						margin-right: 0.75rem;

						::v-deep svg {
							width: 1.125rem;
							height: 1.125rem;
						}
					}

					.segment-content {
						align-items: flex-start;
						text-align: left;
						flex: 1;

						.segment-title {
							font-size: 0.8125rem;
						}

						.segment-subtitle {
							font-size: 0.625rem;
						}
					}
				}
			}
		}
	}
}
</style>
<style lang="scss">
.cluster-list-sidebar {
	.vx-card__body {
		padding: 1rem !important;
	}
	.vx-card .vx-card__footer {
		padding: 0 1rem 0 1rem !important;
	}
}
</style>
